import { create } from 'zustand';
import { dataService } from '../services/dataService.js';
import { processMines } from '../utils/dataHelpers.js';

export const useDataStore = create((set, get) => ({
  mines: [],
  filteredMines: [],
  mineDetails: null,
  countries: [],
  commodities: [],
  countryMap: {},
  commodityMap: {},
  filterOptions: null,
  statistics: null,
  loading: false,
  error: null,
  lazyLoadedStatuses: new Set(),
  allStatusesLoaded: false,

  fetchMines: async (params) => {
    set({ loading: true, error: null });
    try {
      const mines = await dataService.fetchMines(params);
      set({ mines, loading: false });
      return mines;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchMinesByStatus: async (statuses) => {
    set({ loading: true, error: null });
    try {
      const mines = await dataService.fetchMinesByStatus(statuses);
      const state = get();
      const processedMines = processMines(mines, state.commodityMap, state.countryMap);

      const lazyLoadedStatuses = new Set(state.lazyLoadedStatuses);
      statuses.forEach(status => lazyLoadedStatuses.add(status));

      set({
        mines: processedMines,
        filteredMines: processedMines,
        lazyLoadedStatuses,
        loading: false
      });
      return processedMines;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  searchMines: async (query) => {
    set({ loading: true, error: null });
    try {
      const results = await dataService.searchMines(query);
      set({ loading: false });
      return results;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchMineDetails: async (mineId) => {
    set({ loading: true });
    try {
      const details = await dataService.getMineDetails(mineId);
      set({ mineDetails: details, loading: false });
      return details;
    } catch (error) {
      set({ error: error.message, loading: false });
    }
  },

  loadCountries: async () => {
    try {
      const countries = await dataService.getCountries();
      const countryMap = dataService.createCountryMap(countries);
      set({ countries, countryMap });
      return countries;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  loadCommodities: async () => {
    try {
      const commodities = await dataService.getCommodities();
      const commodityMap = dataService.createCommodityMap(commodities);
      set({ commodities, commodityMap });
      return commodities;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  loadFilterOptions: async () => {
    try {
      const filterOptions = await dataService.getFilterOptions();
      set({ filterOptions });
      return filterOptions;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  loadStatistics: async () => {
    try {
      const statistics = await dataService.getStatistics();
      set({ statistics });
      return statistics;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  loadInitialData: async () => {
    set({ loading: true, error: null });
    try {
      const data = await dataService.loadInitialMines();

      const lazyLoadedStatuses = new Set(['Development', 'Active']);

      set({
        mines: data.mines,
        filteredMines: data.mines,
        countries: data.countries,
        commodities: data.commodities,
        countryMap: data.countryMap,
        commodityMap: data.commodityMap,
        lazyLoadedStatuses,
        loading: false
      });

      return data;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  applyFilters: async (filters, bounds = null, zoom = null) => {
    set({ loading: true, error: null });
    try {
      const result = await dataService.applyFilters(filters, bounds, zoom);

      if (result.features) {
        const state = get();
        const mines = result.features.map(f => f.properties);
        const processedMines = processMines(mines, state.commodityMap, state.countryMap);
        set({ filteredMines: processedMines, loading: false });
        return processedMines;
      }

      set({ loading: false });
      return [];
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  setFilteredMines: (mines) => {
    set({ filteredMines: mines });
  },

  setMines: (minesData) => {
    const state = get();
    // Handle GeoJSON response format
    const mines = minesData.features
      ? minesData.features.map(f => f.properties)
      : minesData;
    const processedMines = processMines(mines, state.commodityMap, state.countryMap);
    set({ mines: processedMines, filteredMines: processedMines });
  },

  clearData: () => set({
    mines: [],
    filteredMines: [],
    mineDetails: null,
    error: null,
    lazyLoadedStatuses: new Set(),
    allStatusesLoaded: false
  }),

  clearError: () => set({ error: null }),

  getActiveFilterCount: () => {
    const state = get();
    if (!state.filterOptions) return 0;

    let count = 0;
    Object.entries(state.filterOptions).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        count++;
      }
    });
    return count;
  },

  getMinesByCategory: (category) => {
    const state = get();
    return state.filteredMines.filter(mine => mine.commodity_category === category);
  },

  getMinesByStatus: (status) => {
    const state = get();
    return state.filteredMines.filter(mine => mine.mine_status === status);
  },

  getMinesByCountry: (countryName) => {
    const state = get();
    return state.filteredMines.filter(mine => mine.country_name === countryName);
  },

  // Mine selection for details panel
  selectedMine: null,
  setSelectedMine: (mine) => set({ selectedMine: mine }),

  // Search functionality
  searchResults: [],
  searchMines: async (query, options = {}) => {
    set({ loading: true, error: null });
    try {
      const results = await dataService.searchMines(query, options);
      set({ searchResults: results, loading: false });
      return results;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  clearSearch: () => set({ searchResults: [] }),

  // Total mines count for status bar
  totalMines: 0,
  setTotalMines: (count) => set({ totalMines: count }),

  // Last fetch timestamp
  lastFetch: null,
  markFetch: () => set({ lastFetch: Date.now() })
}));